export type AuthTokenSet = {
	accessToken: string
	refreshToken: string | null
	receivedAt: number
	accessExpiresAt: number | null
	refreshExpiresAt: number | null
}

export type AuthTokenRevision = {
	generation: number
	revision: number
}

export type StoredAuthTokens = AuthTokenSet &
	AuthTokenRevision & {
		storedAt: number
	}

export type AuthStorageChange = AuthTokenRevision & {
	type: 'tokens-updated' | 'logout'
}

export type AuthStorageMode = 'webcrypto-indexeddb' | 'memory-only'

export class AuthStorageTransientError extends Error {
	public readonly cause: unknown

	constructor(message: string, cause?: unknown) {
		super(message)
		this.name = 'AuthStorageTransientError'
		this.cause = cause
	}
}

export class AuthStorageCorruptError extends Error {
	public readonly cause: unknown

	constructor(message: string, cause?: unknown) {
		super(message)
		this.name = 'AuthStorageCorruptError'
		this.cause = cause
	}
}

const isFiniteTimestamp = (value: unknown): value is number =>
	typeof value === 'number' && Number.isFinite(value) && value >= 0

const isNullableTimestamp = (value: unknown): value is number | null =>
	value === null || isFiniteTimestamp(value)

const isRevisionPart = (value: unknown): value is number =>
	typeof value === 'number' && Number.isSafeInteger(value) && value >= 0

export const isAuthTokenSet = (value: unknown): value is AuthTokenSet => {
	if (!value || typeof value !== 'object') return false

	const candidate = value as Partial<AuthTokenSet>
	return (
		typeof candidate.accessToken === 'string' &&
		candidate.accessToken.length > 0 &&
		(candidate.refreshToken === null || typeof candidate.refreshToken === 'string') &&
		isFiniteTimestamp(candidate.receivedAt) &&
		isNullableTimestamp(candidate.accessExpiresAt) &&
		isNullableTimestamp(candidate.refreshExpiresAt)
	)
}

export const isStoredAuthTokens = (value: unknown): value is StoredAuthTokens => {
	if (!isAuthTokenSet(value)) return false

	const candidate = value as Partial<StoredAuthTokens>
	return (
		isFiniteTimestamp(candidate.storedAt) &&
		isRevisionPart(candidate.generation) &&
		isRevisionPart(candidate.revision)
	)
}

export const assertAuthTokenSet = (value: AuthTokenSet): void => {
	if (!isAuthTokenSet(value)) {
		throw new TypeError('Invalid auth token set')
	}
}

export const sameRevision = (left: AuthTokenRevision, right: AuthTokenRevision): boolean =>
	left.generation === right.generation && left.revision === right.revision
